import React, { useState } from 'react';
import { FaSearch, FaBook, FaPuzzlePiece } from 'react-icons/fa';

interface PublicContentManagementProps {
    publicContent: any;
    handleTogglePublic: (type: 'class' | 'quiz', id: string) => void;
}

const PublicContentManagement: React.FC<PublicContentManagementProps> = ({ publicContent, handleTogglePublic }) => {
    const [activeSubTab, setActiveSubTab] = useState<'classes' | 'quizzes'>('classes');
    const [searchTerm, setSearchTerm] = useState('');

    if (!publicContent) return null;

    // Client-side filtering
    const filteredClasses = publicContent.publicClasses.filter((c: any) =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.owner_email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredQuizzes = publicContent.publishedQuizzes.filter((q: any) =>
        q.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.owner_email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Search and Sub-Tabs Control Bar */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700">

                {/* Sub-Tabs Switcher */}
                <div className="flex bg-gray-100 dark:bg-gray-700/50 p-1 rounded-xl w-full md:w-auto">
                    <button
                        onClick={() => setActiveSubTab('classes')}
                        className={`flex-1 md:flex-none px-6 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${activeSubTab === 'classes'
                                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                    >
                        <FaBook /> Classes
                        <span className="ml-1 bg-gray-200 dark:bg-gray-800 px-2 py-0.5 rounded-full text-xs">
                            {publicContent.publicClasses.length}
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveSubTab('quizzes')}
                        className={`flex-1 md:flex-none px-6 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${activeSubTab === 'quizzes'
                                ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                            }`}
                    >
                        <FaPuzzlePiece /> Quizzes
                        <span className="ml-1 bg-gray-200 dark:bg-gray-800 px-2 py-0.5 rounded-full text-xs">
                            {publicContent.publishedQuizzes.length}
                        </span>
                    </button>
                </div>

                {/* Search Bar */}
                <div className="relative w-full md:w-80 group">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <FaSearch className="w-4 h-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                    <input
                        type="text"
                        placeholder={activeSubTab === 'classes' ? "Tìm kiếm class..." : "Tìm kiếm quiz..."}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none dark:text-white dark:placeholder-gray-500"
                    />
                </div>
            </div>

            {/* Content Area */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 min-h-[400px]">

                {/* Classes List */}
                {activeSubTab === 'classes' && (
                    <div className="space-y-3">
                        {filteredClasses.length > 0 ? (
                            filteredClasses.map((cls: any) => (
                                <div key={cls.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-600">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-900 dark:text-white truncate text-lg flex items-center gap-2">
                                            <span className="text-green-500 bg-green-100 dark:bg-green-900/30 p-1.5 rounded-lg text-sm"><FaBook /></span>
                                            {cls.name}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1 ml-9">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Owner:</span>
                                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10 px-2 py-0.5 rounded">{cls.owner_email}</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleTogglePublic('class', cls.id)}
                                        className="w-full sm:w-auto px-5 py-2.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg font-semibold transition-all text-sm shadow-sm whitespace-nowrap"
                                    >
                                        Ẩn Class
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-20 flex flex-col items-center opacity-60">
                                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-3xl mb-4 text-gray-400">📚</div>
                                <p className="text-gray-500 dark:text-gray-400 font-medium">
                                    {searchTerm ? 'Không tìm thấy kết quả nào' : 'Hiện không có lớp học công khai nào'}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Quizzes List */}
                {activeSubTab === 'quizzes' && (
                    <div className="space-y-3">
                        {filteredQuizzes.length > 0 ? (
                            filteredQuizzes.map((quiz: any) => (
                                <div key={quiz.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-transparent hover:border-gray-200 dark:hover:border-gray-600">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-gray-900 dark:text-white truncate text-lg flex items-center gap-2">
                                            <span className="text-purple-500 bg-purple-100 dark:bg-purple-900/30 p-1.5 rounded-lg text-sm"><FaPuzzlePiece /></span>
                                            {quiz.title}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1 ml-9">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">Owner:</span>
                                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10 px-2 py-0.5 rounded">{quiz.owner_email}</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleTogglePublic('quiz', quiz.id)}
                                        className="w-full sm:w-auto px-5 py-2.5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/50 rounded-lg font-semibold transition-all text-sm shadow-sm whitespace-nowrap"
                                    >
                                        Gỡ Công Bố
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-20 flex flex-col items-center opacity-60">
                                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-3xl mb-4 text-gray-400">🧩</div>
                                <p className="text-gray-500 dark:text-gray-400 font-medium">
                                    {searchTerm ? 'Không tìm thấy kết quả nào' : 'Hiện không có quiz đã công bố'}
                                </p>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
};

export default PublicContentManagement;
